import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import './SuppliersPopup.css';

// ─── helpers ─────────────────────────────────────────────────────────────────
const EMPTY_MATERIAL = { supplierName: '', supplierPhone: '', supplierEmail: '', typeIDs: [] };
const EMPTY_SEEDS    = { supplierName: '', supplierPhone: '', supplierMail: '' };

function Field({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div className="sp-field">
      <label className="sp-label">{label}</label>
      <input
        className="sp-input"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

// ─── Material Supplier Form ───────────────────────────────────────────────────
function MaterialSupplierForm({ initial, materialTypes, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_MATERIAL);

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  const toggleType = (id) =>
    setForm(f => ({
      ...f,
      typeIDs: f.typeIDs.includes(id) ? f.typeIDs.filter(t => t !== id) : [...f.typeIDs, id],
    }));

  return (
    <div className="sp-form">
      <Field label="Name *"  value={form.supplierName}  onChange={set('supplierName')}  placeholder="Supplier name" />
      <Field label="Phone"   value={form.supplierPhone || ''} onChange={set('supplierPhone')} placeholder="+1 555 000 0000" />
      <Field label="Email"   value={form.supplierEmail || ''} onChange={set('supplierEmail')} type="email" placeholder="supplier@example.com" />

      <div className="sp-field">
        <label className="sp-label">Material Types</label>
        <div className="sp-type-grid">
          {materialTypes.map(mt => (
            <label key={mt.materialtypeID} className="sp-type-chip">
              <input
                type="checkbox"
                checked={form.typeIDs.includes(mt.materialtypeID)}
                onChange={() => toggleType(mt.materialtypeID)}
              />
              {mt.materialName}
            </label>
          ))}
        </div>
      </div>

      <div className="sp-form-actions">
        <button className="sp-btn sp-btn--ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <button
          className="sp-btn sp-btn--primary"
          disabled={saving || !form.supplierName.trim()}
          onClick={() => onSave(form)}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ─── Seeds Supplier Form ──────────────────────────────────────────────────────
function SeedsSupplierForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_SEEDS);
  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div className="sp-form">
      <Field label="Name *"  value={form.supplierName}        onChange={set('supplierName')}  placeholder="Supplier name" />
      <Field label="Phone"   value={form.supplierPhone || ''} onChange={set('supplierPhone')} placeholder="+1 555 000 0000" />
      <Field label="Email"   value={form.supplierMail  || ''} onChange={set('supplierMail')}  type="email" placeholder="supplier@example.com" />

      <div className="sp-form-actions">
        <button className="sp-btn sp-btn--ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <button
          className="sp-btn sp-btn--primary"
          disabled={saving || !form.supplierName.trim()}
          onClick={() => onSave(form)}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ─── Confirm Delete ───────────────────────────────────────────────────────────
function ConfirmDelete({ name, onConfirm, onCancel, deleting }) {
  return (
    <div className="sp-confirm">
      <p>Delete <strong>{name}</strong>? This cannot be undone.</p>
      <div className="sp-form-actions">
        <button className="sp-btn sp-btn--ghost" onClick={onCancel} disabled={deleting}>Cancel</button>
        <button className="sp-btn sp-btn--danger" onClick={onConfirm} disabled={deleting}>
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  );
}

// ─── Material Suppliers Tab ───────────────────────────────────────────────────
function MaterialSuppliersTab({ materialTypes }) {
  const [suppliers, setSuppliers]   = useState([]);
  const [loading,   setLoading]     = useState(true);
  const [mode,      setMode]        = useState(null); // null | 'add' | { edit: supplier } | { del: supplier }
  const [saving,    setSaving]      = useState(false);
  const [error,     setError]       = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/material-orders/suppliers/material')
      .then(r => r.data)
      .then(data => { setSuppliers(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    setSaving(true); setError('');
    try {
      const isEdit = mode?.edit;
      const url  = isEdit
        ? `/api/material-orders/suppliers/material/${isEdit.supplierID}`
        : `/api/material-orders/suppliers/material`;
      await api[isEdit ? 'put' : 'post'](url, form);
      setMode(null);
      load();
    } catch (e) { setError(e.response ? (e.response.data?.error || 'Error') : e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (supplier) => {
    setSaving(true); setError('');
    try {
      await api.delete(`/api/material-orders/suppliers/material/${supplier.supplierID}`);
      setMode(null);
      load();
    } catch (e) { setError(e.response ? (e.response.data?.error || 'Error') : e.message); }
    finally { setSaving(false); }
  };

  if (mode === 'add') return (
    <MaterialSupplierForm
      materialTypes={materialTypes}
      onSave={handleSave}
      onCancel={() => setMode(null)}
      saving={saving}
    />
  );
  if (mode?.edit) return (
    <MaterialSupplierForm
      initial={mode.edit}
      materialTypes={materialTypes}
      onSave={handleSave}
      onCancel={() => setMode(null)}
      saving={saving}
    />
  );
  if (mode?.del) return (
    <ConfirmDelete
      name={mode.del.supplierName}
      onConfirm={() => handleDelete(mode.del)}
      onCancel={() => setMode(null)}
      deleting={saving}
    />
  );

  return (
    <div className="sp-tab-content">
      <div className="sp-tab-toolbar">
        <span className="sp-count">{suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}</span>
        <button className="sp-btn sp-btn--primary sp-btn--sm" onClick={() => setMode('add')}>
          <i className="fas fa-plus"></i> Add Supplier
        </button>
      </div>
      {error && <div className="sp-error">{error}</div>}
      {loading ? (
        <div className="sp-loading">Loading…</div>
      ) : suppliers.length === 0 ? (
        <div className="sp-empty">No material suppliers yet.</div>
      ) : (
        <table className="sp-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Material Types</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map(s => (
              <tr key={s.supplierID}>
                <td className="sp-name">{s.supplierName}</td>
                <td>{s.supplierPhone || <span className="sp-empty-cell">—</span>}</td>
                <td>{s.supplierEmail || <span className="sp-empty-cell">—</span>}</td>
                <td>
                  {s.typeNames.length > 0
                    ? s.typeNames.map((n, i) => (
                        <span key={i} className="sp-type-badge">{n}</span>
                      ))
                    : <span className="sp-empty-cell">—</span>
                  }
                </td>
                <td className="sp-actions">
                  <button className="sp-icon-btn" title="Edit" onClick={() => setMode({ edit: s })}>
                    <i className="fas fa-pen"></i>
                  </button>
                  <button className="sp-icon-btn sp-icon-btn--danger" title="Delete" onClick={() => setMode({ del: s })}>
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Seeds Suppliers Tab ──────────────────────────────────────────────────────
function SeedsSuppliersTab() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [mode,      setMode]      = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/material-orders/suppliers/seeds')
      .then(r => r.data)
      .then(data => { setSuppliers(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    setSaving(true); setError('');
    try {
      const isEdit = mode?.edit;
      const url  = isEdit
        ? `/api/material-orders/suppliers/seeds/${isEdit.supplierID}`
        : `/api/material-orders/suppliers/seeds`;
      await api[isEdit ? 'put' : 'post'](url, form);
      setMode(null);
      load();
    } catch (e) { setError(e.response ? (e.response.data?.error || 'Error') : e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (supplier) => {
    setSaving(true); setError('');
    try {
      await api.delete(`/api/material-orders/suppliers/seeds/${supplier.supplierID}`);
      setMode(null);
      load();
    } catch (e) { setError(e.response ? (e.response.data?.error || 'Error') : e.message); }
    finally { setSaving(false); }
  };

  if (mode === 'add') return (
    <SeedsSupplierForm onSave={handleSave} onCancel={() => setMode(null)} saving={saving} />
  );
  if (mode?.edit) return (
    <SeedsSupplierForm initial={mode.edit} onSave={handleSave} onCancel={() => setMode(null)} saving={saving} />
  );
  if (mode?.del) return (
    <ConfirmDelete
      name={mode.del.supplierName}
      onConfirm={() => handleDelete(mode.del)}
      onCancel={() => setMode(null)}
      deleting={saving}
    />
  );

  return (
    <div className="sp-tab-content">
      <div className="sp-tab-toolbar">
        <span className="sp-count">{suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}</span>
        <button className="sp-btn sp-btn--primary sp-btn--sm" onClick={() => setMode('add')}>
          <i className="fas fa-plus"></i> Add Supplier
        </button>
      </div>
      {error && <div className="sp-error">{error}</div>}
      {loading ? (
        <div className="sp-loading">Loading…</div>
      ) : suppliers.length === 0 ? (
        <div className="sp-empty">No seeds suppliers yet.</div>
      ) : (
        <table className="sp-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map(s => (
              <tr key={s.supplierID}>
                <td className="sp-name">{s.supplierName}</td>
                <td>{s.supplierPhone || <span className="sp-empty-cell">—</span>}</td>
                <td>{s.supplierMail  || <span className="sp-empty-cell">—</span>}</td>
                <td className="sp-actions">
                  <button className="sp-icon-btn" title="Edit" onClick={() => setMode({ edit: s })}>
                    <i className="fas fa-pen"></i>
                  </button>
                  <button className="sp-icon-btn sp-icon-btn--danger" title="Delete" onClick={() => setMode({ del: s })}>
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Fertilizer Suppliers Tab ─────────────────────────────────────────────────
const EMPTY_FERT = { supplierName: '' };

function FertilizerSuppliersTab() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [mode,      setMode]      = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/fert-suppliers')
      .then(r => r.data)
      .then(data => { setSuppliers(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    setSaving(true); setError('');
    try {
      const isEdit = mode?.edit;
      const url    = isEdit
        ? `/api/fert-suppliers/${isEdit.supplierID}`
        : `/api/fert-suppliers`;
      await api[isEdit ? 'put' : 'post'](url, form);
      setMode(null);
      load();
    } catch (e) { setError(e.response ? (e.response.data?.error || 'Error') : e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (supplier) => {
    setSaving(true); setError('');
    try {
      await api.delete(`/api/fert-suppliers/${supplier.supplierID}`);
      setMode(null);
      load();
    } catch (e) { setError(e.response ? (e.response.data?.error || 'Error') : e.message); }
    finally { setSaving(false); }
  };

  const FertForm = ({ initial }) => {
    const [form, setForm] = useState(initial || EMPTY_FERT);
    return (
      <div className="sp-form">
        <Field label="Name *" value={form.supplierName} onChange={v => setForm(f => ({ ...f, supplierName: v }))} placeholder="Supplier name" />
        <div className="sp-form-actions">
          <button className="sp-btn sp-btn--ghost" onClick={() => setMode(null)} disabled={saving}>Cancel</button>
          <button
            className="sp-btn sp-btn--primary"
            disabled={saving || !form.supplierName.trim()}
            onClick={() => handleSave(form)}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    );
  };

  if (mode === 'add') return <FertForm />;
  if (mode?.edit)    return <FertForm initial={mode.edit} />;
  if (mode?.del) return (
    <ConfirmDelete
      name={mode.del.supplierName}
      onConfirm={() => handleDelete(mode.del)}
      onCancel={() => setMode(null)}
      deleting={saving}
    />
  );

  return (
    <div className="sp-tab-content">
      <div className="sp-tab-toolbar">
        <span className="sp-count">{suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}</span>
        <button className="sp-btn sp-btn--primary sp-btn--sm" onClick={() => setMode('add')}>
          <i className="fas fa-plus"></i> Add Supplier
        </button>
      </div>
      {error && <div className="sp-error">{error}</div>}
      {loading ? (
        <div className="sp-loading">Loading…</div>
      ) : suppliers.length === 0 ? (
        <div className="sp-empty">No fertilizer suppliers yet.</div>
      ) : (
        <table className="sp-table">
          <thead>
            <tr><th>Name</th><th></th></tr>
          </thead>
          <tbody>
            {suppliers.map(s => (
              <tr key={s.supplierID}>
                <td className="sp-name">{s.supplierName}</td>
                <td className="sp-actions">
                  <button className="sp-icon-btn" title="Edit" onClick={() => setMode({ edit: s })}>
                    <i className="fas fa-pen"></i>
                  </button>
                  <button className="sp-icon-btn sp-icon-btn--danger" title="Delete" onClick={() => setMode({ del: s })}>
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Ready Products Suppliers Tab ────────────────────────────────────────────
const EMPTY_RP = { supplierName: '', supplierPhone: '', supplierEmail: '' };

function ReadyProductsSuppliersTab() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [mode,      setMode]      = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/material-orders/suppliers/ready-products')
      .then(r => r.data)
      .then(data => { setSuppliers(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    setSaving(true); setError('');
    try {
      const isEdit = mode?.edit;
      const url  = isEdit
        ? `/api/material-orders/suppliers/ready-products/${isEdit.supplierID}`
        : `/api/material-orders/suppliers/ready-products`;
      await api[isEdit ? 'put' : 'post'](url, form);
      setMode(null);
      load();
    } catch (e) { setError(e.response ? (e.response.data?.error || 'Error') : e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (supplier) => {
    setSaving(true); setError('');
    try {
      await api.delete(`/api/material-orders/suppliers/ready-products/${supplier.supplierID}`);
      setMode(null);
      load();
    } catch (e) { setError(e.response ? (e.response.data?.error || 'Error') : e.message); }
    finally { setSaving(false); }
  };

  const RPForm = ({ initial }) => {
    const [form, setForm] = useState(initial || EMPTY_RP);
    const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));
    return (
      <div className="sp-form">
        <Field label="Name *"  value={form.supplierName}        onChange={set('supplierName')}  placeholder="Supplier name" />
        <Field label="Phone"   value={form.supplierPhone || ''} onChange={set('supplierPhone')} placeholder="+1 555 000 0000" />
        <Field label="Email"   value={form.supplierEmail || ''} onChange={set('supplierEmail')} type="email" placeholder="supplier@example.com" />
        <div className="sp-form-actions">
          <button className="sp-btn sp-btn--ghost" onClick={() => setMode(null)} disabled={saving}>Cancel</button>
          <button
            className="sp-btn sp-btn--primary"
            disabled={saving || !form.supplierName.trim()}
            onClick={() => handleSave(form)}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    );
  };

  if (mode === 'add') return <RPForm />;
  if (mode?.edit)    return <RPForm initial={mode.edit} />;
  if (mode?.del) return (
    <ConfirmDelete
      name={mode.del.supplierName}
      onConfirm={() => handleDelete(mode.del)}
      onCancel={() => setMode(null)}
      deleting={saving}
    />
  );

  return (
    <div className="sp-tab-content">
      <div className="sp-tab-toolbar">
        <span className="sp-count">{suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}</span>
        <button className="sp-btn sp-btn--primary sp-btn--sm" onClick={() => setMode('add')}>
          <i className="fas fa-plus"></i> Add Supplier
        </button>
      </div>
      {error && <div className="sp-error">{error}</div>}
      {loading ? (
        <div className="sp-loading">Loading…</div>
      ) : suppliers.length === 0 ? (
        <div className="sp-empty">No ready products suppliers yet.</div>
      ) : (
        <table className="sp-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map(s => (
              <tr key={s.supplierID}>
                <td className="sp-name">{s.supplierName}</td>
                <td>{s.supplierPhone || <span className="sp-empty-cell">—</span>}</td>
                <td>{s.supplierEmail || <span className="sp-empty-cell">—</span>}</td>
                <td className="sp-actions">
                  <button className="sp-icon-btn" title="Edit" onClick={() => setMode({ edit: s })}>
                    <i className="fas fa-pen"></i>
                  </button>
                  <button className="sp-icon-btn sp-icon-btn--danger" title="Delete" onClick={() => setMode({ del: s })}>
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Main Popup ───────────────────────────────────────────────────────────────
const TABS = ['Materials', 'Seeds', 'Fertilizers', 'Ready Products'];

export default function SuppliersPopup({ onClose, materialTypes = [] }) {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="sp-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sp-modal">
        <div className="sp-header">
          <h2 className="sp-title">
            <i className="fas fa-truck" style={{ marginRight: 8, color: '#6366f1' }}></i>
            Manage Suppliers
          </h2>
          <button className="sp-close" onClick={onClose}>&times;</button>
        </div>

        <div className="sp-tabs">
          {TABS.map((t, i) => (
            <button
              key={t}
              className={`sp-tab-btn${activeTab === i ? ' active' : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {i === 0
                ? <><i className="fas fa-box" style={{ marginRight: 6 }}></i>{t}</>
                : i === 1
                ? <><i className="fas fa-seedling" style={{ marginRight: 6 }}></i>{t}</>
                : i === 2
                ? <><i className="fas fa-flask" style={{ marginRight: 6 }}></i>{t}</>
                : <><i className="fas fa-tag" style={{ marginRight: 6 }}></i>{t}</>
              }
            </button>
          ))}
        </div>

        <div className="sp-body">
          {activeTab === 0
            ? <MaterialSuppliersTab materialTypes={materialTypes} />
            : activeTab === 1
            ? <SeedsSuppliersTab />
            : activeTab === 2
            ? <FertilizerSuppliersTab />
            : <ReadyProductsSuppliersTab />
          }
        </div>
      </div>
    </div>
  );
}
